import { useActiveProject } from "@/features/project/ActiveProject";
import {
  HistoryEntryTypeEnum,
  createHistoryEntry,
  useProfile,
} from "@repo/api";
import { Button, Option, Select, Text, TextArea } from "@repo/skapa";
import { T, t } from "@repo/translation";
import { ModalSheet, SectionContainer, useToast } from "@repo/ui";
import { getErrorMessage, trackAnalytics } from "@repo/utils";
import { type ChangeEvent, useCallback, useState } from "react";

type SpokeWithCustomerEntryType =
  | HistoryEntryTypeEnum.CUSTOMER_CALLED_IN
  | HistoryEntryTypeEnum.CALLED_THE_CUSTOMER
  | HistoryEntryTypeEnum.WALK_IN_CUSTOMER
  | HistoryEntryTypeEnum.CUSTOMER_EMAILED;

const interactions: SpokeWithCustomerEntryType[] = [
  HistoryEntryTypeEnum.CUSTOMER_CALLED_IN,
  HistoryEntryTypeEnum.CALLED_THE_CUSTOMER,
  HistoryEntryTypeEnum.WALK_IN_CUSTOMER,
  HistoryEntryTypeEnum.CUSTOMER_EMAILED,
];

interface SpokeWithCustomerModalProps {
  isOpen: boolean;
  closeModal: () => void;
  onBack?: () => void;
}

export const SpokeWithCustomerModal = ({
  isOpen,
  closeModal,
  onBack,
}: SpokeWithCustomerModalProps) => {
  const [reason, setReason] = useState<SpokeWithCustomerEntryType>(
    interactions[0]
  );
  const [comment, setComment] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const { opportunity } = useActiveProject();
  const { data: profile } = useProfile();
  const { updateToast } = useToast();

  const isValid = reason && opportunity?.id !== undefined && comment?.length;

  const onAdd = useCallback(() => {
    if (!isValid) {
      return;
    }
    setIsLoading(true);
    createHistoryEntry(
      {
        entryType: reason,
        note: comment,
        createdBy: profile?.mail ?? "",
      },
      opportunity.id
    )
      .then(() => {
        setIsLoading(false);
        updateToast({
          title: t("prompt.task_saved"),
        });
        closeModal();
      })
      .catch((err) => {
        setIsLoading(false);
        updateToast({
          title: t("prompt.note_saved_failed"),
          message: getErrorMessage(err),
          type: "error",
        });
      });
  }, [comment, opportunity, profile, updateToast, closeModal, isValid, reason]);

  return (
    <ModalSheet
      title={t("action.spoke_with_customer")}
      visible={isOpen}
      onClose={closeModal}
      onBack={onBack}
      footer={
        <Button
          type="primary"
          onClick={() => {
            onAdd();
            trackAnalytics("add_spoke_with_customer_action");
          }}
          text={t("action.add")}
          loading={isLoading}
          disabled={!isValid}
        />
      }
    >
      <SectionContainer>
        <Text textStyle="Heading.XS">
          <T translationKey="prompt.type_of_interaction" />
        </Text>
        <Select
          id="type_of_interaction"
          label={t("label.choose_type_of_interaction")}
          value={reason}
          onChange={(e: ChangeEvent<HTMLSelectElement>) => {
            setReason(e.currentTarget.value as SpokeWithCustomerEntryType);
            trackAnalytics("what_type_of_interaction_did_you_have");
          }}
          required
        >
          {interactions.map((interaction) => (
            <Option
              value={interaction}
              key={interaction}
              name={t(`interaction.${interaction.toLowerCase()}`)}
            />
          ))}
        </Select>
        <TextArea
          id="comment"
          label={t("label.comment")}
          maxLength={400}
          onChange={(e: ChangeEvent<HTMLTextAreaElement>) => {
            setComment(e.target.value);
            trackAnalytics("add_comment");
          }}
          required
        />
      </SectionContainer>
    </ModalSheet>
  );
};
